// Pure extraction of MessagesScreen.tsx's message-bunching predicate +
// grouping loop (Settings > Appearance / Channel edit form's "Message
// bunching" section) so a read-only screen (the public channel page) can
// group consecutive same-sender messages the same way the private chat
// does, without pulling in that screen's BunchStack tap-to-expand UI or
// round/mask-aware state - a public page has neither rounds nor an
// expand affordance, it just wants the same "who said what, grouped"
// visual read.
//
// Any object with sender/date works (Message, FetchedSignal, ...) - kept
// generic rather than importing MessagesScreen.tsx's private Message-typed
// isBunched so this has no dependency on that screen at all.
interface Bunchable {
  sender: string;
  date: string;
}

function isSameDay(isoA: string, isoB: string) {
  return new Date(isoA).toDateString() === new Date(isoB).toDateString();
}

// intervalSeconds <= 0 means bunching is off (same contract as the
// Channel.bunchingInterval field it's read from).
function isBunched<T extends Bunchable>(message: T, previous: T | undefined, intervalSeconds: number): boolean {
  if (intervalSeconds <= 0 || !previous) return false;
  if (message.sender.trim().toLocaleLowerCase() !== previous.sender.trim().toLocaleLowerCase()) return false;
  if (!isSameDay(message.date, previous.date)) return false;
  const gapMs = Math.abs(new Date(previous.date).getTime() - new Date(message.date).getTime());
  return Number.isFinite(gapMs) && gapMs <= intervalSeconds * 1000;
}

// Same MAX_BUNCH_SIZE cap and rationale as MessagesScreen.tsx: an
// automated/chatty sender posting seconds apart for hours would otherwise
// fold a whole page's worth of messages into one group, which is both
// useless as a group and defeats any "load more" pagination relying on
// rendered height.
const MAX_BUNCH_SIZE = 20;

// Groups a chronologically-ordered (newest-first or oldest-first, doesn't
// matter - grouping only compares each message to its immediate neighbor
// in the array) list into consecutive same-sender runs per
// isBunched/intervalSeconds. intervalSeconds <= 0 returns one group per
// message (bunching off).
export function bunchMessages<T extends Bunchable>(messages: T[], intervalSeconds: number): T[][] {
  const groups: T[][] = [];
  messages.forEach((message, i) => {
    const prev = messages[i - 1];
    const lastGroup = groups[groups.length - 1];
    if (i > 0 && lastGroup && lastGroup.length < MAX_BUNCH_SIZE && isBunched(message, prev, intervalSeconds)) {
      lastGroup.push(message);
    } else {
      groups.push([message]);
    }
  });
  return groups;
}
